import { Field, FieldProps, Formik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, CardBody, Col, Form, Label, Row } from "reactstrap";
import Select from "react-select";
import * as Yup from "yup";

import FuelEUResultsModal from "./FuelEUResultsModal";
import { FuelMixFields, LcvEcho, fuelMixSchema } from "./FuelEUFuelRows";
import QuantityField from "./QuantityField";
import {
  MAX_E_VALUE_DECIMALS,
  MAX_E_VALUE_DIGITS,
  MAX_LCV_DECIMALS,
  MAX_LCV_DIGITS,
  MAX_OPS_DECIMALS,
  MAX_OPS_DIGITS,
  apiErrorMessage,
  errorToast,
  loadingToast,
} from "./common";
import { useIsCompact, useIsMobile } from "./useResponsive";
import {
  FuelEUResult,
  FuelEUTargets,
  FuelFactor,
  balanceRatingClass,
  emptyFuelRow,
  formatBalance,
  formatEnergy,
  formatEuro,
  formatNumber,
  BLEND_FOSSIL_FACTOR_IDS,
  bioBlendOption,
  groupFuelFactors,
  loadFuelEUTargets,
  loadFuelFactors,
  buildPayload,
  needsOverride,
  postPreview,
  usesEValue,
  saveFuelEU,
} from "./fueleu";

/**
 * FuelEU Maritime compliance-balance calculator.
 *
 * Where the CII tab computes its answer in the browser and posts the inputs, this
 * one posts the inputs and renders the answer. Every number on screen comes from
 * `fueleu_calculator/preview/`, which runs the same `ecosail/fueleu.py` the saved
 * calculation and the stored results run — so what the user is shown and what
 * gets recorded cannot disagree.
 */

/** How long the form sits still before it asks the API to recompute. */
const PREVIEW_DEBOUNCE_MS = 400;

const FuelEUCalculator = () => {
  const isMobile = useIsMobile();
  const isCompact = useIsCompact();

  const [factors, setFactors] = useState<FuelFactor[]>([]);
  const [targets, setTargets] = useState<FuelEUTargets | null>(null);
  const [preview, setPreview] = useState<FuelEUResult | null>(null);
  const [previewError, setPreviewError] = useState<string>("");
  const [modalState, setModalState] = useState(false);
  const [recordId, setRecordId] = useState(0);
  const [savedResult, setSavedResult] = useState<FuelEUResult | null>(null);

  useEffect(() => {
    loadFuelFactors().then(setFactors);
    loadFuelEUTargets().then(setTargets);
  }, []);

  /**
   * The picker's options: the factor table plus the synthetic Bio blend entry.
   * The blend is not a factor row - it has no emission factors of its own, only
   * the two fuels it is made of - so it is added here rather than seeded.
   */
  const pickerOptions = useMemo(
    () => [...factors, bioBlendOption()],
    [factors]
  );

  const factorsById = useMemo(() => {
    const map = new Map<number, FuelFactor>();
    pickerOptions.forEach((factor) => map.set(factor.id, factor));
    return map;
  }, [pickerOptions]);

  const fuelGroups = useMemo(
    () => groupFuelFactors(pickerOptions), [pickerOptions]);

  /** The two fossil fuels a bio-diesel blend may be cut with. */
  const blendFossilOptions = useMemo(
    () => factors.filter((f) => BLEND_FOSSIL_FACTOR_IDS.includes(f.id)),
    [factors]
  );

  /**
   * Only the fuels worth substituting *in*. Offering the fossil rows here would
   * let the optimiser be asked how much heavy fuel oil to add to fix a heavy
   * fuel oil problem, which the API answers correctly ("no cleaner than what it
   * would replace") but should never have been asked.
   */
  const alternativeGroups = useMemo(
    () =>
      groupFuelFactors(factors).filter((group) => group.label !== "Fossil"),
    [factors]
  );

  const yearOptions = useMemo(
    () =>
      (targets?.targets ?? []).map((target) => ({
        value: target.year,
        label: `${target.year}  —  target ${formatNumber(
          target.target_intensity,
          2
        )} gCO₂eq/MJ  (−${formatNumber(target.reduction * 100, 1)}%)`,
      })),
    [targets]
  );

  const initialValues = {
    name_of_vessel: "",
    imo_number: "",
    year: 2025,
    fuels: [emptyFuelRow()],
    // kWh, not MJ - see the OPS field. buildPayload converts.
    ops_energy_kwh: 0,
    alternative_fuel: null as number | null,
    alternative_intensity_override: "",
    alternative_lcv_override: "",
    alternative_e_value: "",
  };

  /* --------------------------------------------------- required values ---
   * Which optional boxes a row actually has to have filled is decided by
   * `fuelMixSchema`, shared with the daily calculator - the two forms ask for
   * the same mix and must demand the same things of it. Only the fields unique
   * to this form are validated here.
   */
  const isFilled = (value: any) => String(value ?? "").trim() !== "";

  const alternativeFactorFor = (values: any) =>
    values?.alternative_fuel
      ? factorsById.get(values.alternative_fuel)
      : undefined;

  const validationSchema = Yup.object({
    name_of_vessel: Yup.string().required("This field is required!"),
    imo_number: Yup.string()
      .matches(/^\d{7}$/, "Please enter 7 digits")
      .required("This field is required!"),
    year: Yup.number().required("This field is required!"),

    // The optimiser's candidate. Optional as a whole - but once chosen, it has
    // to be priceable, or the block reports "select an alternative fuel" at a
    // fuel that is plainly selected.
    alternative_e_value: Yup.mixed().test(
      "alternative-e-required",
      "Required — Annex II derives this fuel's WtT from its E value.",
      function (value) {
        const factor = alternativeFactorFor(this.parent);
        return !usesEValue(factor) || isFilled(value);
      }
    ),
    alternative_intensity_override: Yup.mixed().test(
      "alternative-intensity-required",
      "Required — Annex II publishes no default for this fuel.",
      function (value) {
        const factor = alternativeFactorFor(this.parent);
        return (
          !factor || !needsOverride(factor) || usesEValue(factor) || isFilled(value)
        );
      }
    ),
    alternative_lcv_override: Yup.mixed().test(
      "alternative-lcv-required",
      "Required — the factor table has no LCV for this fuel.",
      function (value) {
        const factor = alternativeFactorFor(this.parent);
        return (
          !factor || !needsOverride(factor) || !!factor.lcv || isFilled(value)
        );
      }
    ),

    fuels: fuelMixSchema(factorsById),
  });

  return (
    <CardBody>
      <h5 className="mt-1 mb-2">FuelEU Maritime Calculator</h5>
      <p className="fueleu-intro">
        Attained GHG intensity, compliance balance and Annex IV penalty for one
        reporting year, plus the alternative fuel needed to reach a zero balance.
      </p>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: any, actions: any) => {
          actions.setSubmitting(false);
          loadingToast("Please wait! we are calculating...");
          saveFuelEU(values)
            .then((data: any) => {
              setRecordId(data.id);
              setSavedResult(data.result);
              setModalState(true);
            })
            .catch((err: any) => {
              errorToast("Error! " + apiErrorMessage(err));
            });
        }}
      >
        {(props: any) => {
          const alternativeFactor = props.values.alternative_fuel
            ? factorsById.get(props.values.alternative_fuel)
            : undefined;

          return (
          <Form onSubmit={props.handleSubmit} noValidate autoComplete="off">
            <PreviewSync
              values={props.values}
              onResult={setPreview}
              onError={setPreviewError}
            />

            <Row className={isMobile ? "" : "mb-3"}>
              <Col xs={12} md={6} lg={4}>
                <Label className="mb-0 asteric">Name of vessel</Label>
                <Field
                  type="text"
                  id="name_of_vessel"
                  name="name_of_vessel"
                  className="form-control mb-2"
                />
                {props.errors?.name_of_vessel && (
                  <Label className="error">{props.errors.name_of_vessel}</Label>
                )}
              </Col>
              <Col xs={12} md={6} lg={4}>
                <Label className="mb-0 asteric">IMO number</Label>
                <Field
                  type="text"
                  id="imo_number"
                  name="imo_number"
                  className="form-control mb-2"
                />
                {props.errors?.imo_number && (
                  <Label className="error">{props.errors.imo_number}</Label>
                )}
              </Col>
              <Col xs={12} md={12} lg={4}>
                <Label className="mb-0 asteric">Reporting year</Label>
                <Field name="year">
                  {({ field, form }: FieldProps) => (
                    <Select
                      options={yearOptions}
                      value={yearOptions.find(
                        (option) => option.value === field.value
                      )}
                      onChange={(option: any) =>
                        form.setFieldValue(field.name, option?.value ?? null)
                      }
                      menuPortalTarget={document.body}
                      className="mb-2"
                      placeholder="Reporting year"
                    />
                  )}
                </Field>
                {props.errors?.year && (
                  <Label className="error">{props.errors.year}</Label>
                )}
              </Col>
            </Row>

            {/* ------------------------------------------------ the fuel mix */}
            <h6 className="mt-4 mb-1">
              Estimated fuel consumption during the planned voyage
            </h6>
            {/* The scope warning leads, and is styled as a warning rather than a
                hint. The calculator takes the quantity at face value — it has no
                voyage legs to apply the 50/100% factors itself — so an operator
                who enters raw bunker figures gets a compliance balance that is
                simply too large, with nothing on screen to suggest it. */}
            <p className="fueleu-scope">
              Ensure you enter only the part of total fuel applicable to the EU
              compliance.
              <span className="fueleu-scope__formula">
                (FCvoyage × 100% or 50% + FCport × 100% or 50%)
              </span>
            </p>

            <FuelMixFields
              form={props}
              factorsById={factorsById}
              fuelGroups={fuelGroups}
              blendFossilOptions={blendFossilOptions}
              isCompact={isCompact}
              unitLabel="mt"
              quantityLabel="Quantity"
            />

            {/* One figure for the whole mix, not one per fuel. This is what
                FuelEU Annex I Eq.(1) actually produces — a single
                energy-weighted intensity — and it is computed by summing the
                equation's two numerators across every fuel, never by averaging
                the per-fuel values this used to display. */}
            <MixIntensity result={preview} />

            {typeof props.errors?.fuels === "string" && (
              <Label className="error d-block mt-2">{props.errors.fuels}</Label>
            )}

            {/* ------------------------------------------ zero-carbon energy */}
            <h6 className="mt-4 mb-1">OPS used (optional)</h6>
            <p className="fueleu-hint">
              Electricity delivered to the ship per OPS connection.
            </p>
            <Row className={isMobile ? "" : "mb-3"}>
              <Col xs={12} md={6} lg={4}>
                <Label className="mb-0">On-shore power supply (OPS)</Label>
                {/* Entered in kWh, which is the unit an OPS connection is
                    metered and invoiced in. The API works in MJ throughout, so
                    buildPayload multiplies by 3.6 on the way out — hence the
                    field name, which exists to stop anyone reading this value
                    as the megajoules the API field of the same idea holds. */}
                <QuantityField
                  name="ops_energy_kwh"
                  unit="kWh"
                  maxWholeDigits={MAX_OPS_DIGITS}
                  maxDecimals={MAX_OPS_DECIMALS}
                />
              </Col>
            </Row>

            {/* --------------------------------------------- the optimiser */}
            <h6 className="mt-4 mb-1">Reach a zero compliance balance</h6>
            <p className="fueleu-hint">
              Pick the fuel you would blend in. The calculator solves for the
              quantity that replaces enough of your dirtiest fossil fuel to put
              the balance at zero.
            </p>
            <Row className={isMobile ? "" : "mb-3"}>
              <Col xs={12} md={6} lg={5}>
                <Label className="mb-0">Alternative fuel</Label>
                <Field name="alternative_fuel">
                  {({ field, form }: FieldProps) => {
                    const chosen = field.value
                      ? factorsById.get(field.value)
                      : undefined;
                    return (
                      <Select
                        options={alternativeGroups}
                        value={chosen ?? null}
                        getOptionLabel={(option: any) => option.label}
                        getOptionValue={(option: any) => String(option.id)}
                        onChange={(option: any) => {
                          form.setFieldValue(field.name, option?.id ?? null);
                          form.setFieldValue(
                            "alternative_intensity_override",
                            ""
                          );
                          form.setFieldValue("alternative_lcv_override", "");
                          form.setFieldValue("alternative_e_value", "");
                        }}
                        isClearable
                        menuPortalTarget={document.body}
                        className="mb-2"
                        placeholder="Select an alternative fuel"
                      />
                    );
                  }}
                </Field>
              </Col>
              {needsOverride(alternativeFactor) && (
                <>
                  <Col xs={12} md={6} lg={4}>
                    {/* Same split as a bunkered fuel: Annex II derives a
                        biofuel's well-to-tank term from E and publishes one
                        outright for everything else. The optimiser has to price
                        a candidate the way it would price that fuel once
                        bunkered, or the quantity it recommends would not
                        produce the balance it promises. */}
                    {usesEValue(alternativeFactor) ? (
                      <>
                        <Label className="mb-0 asteric">
                          E value (RED II Annex V)
                        </Label>
                        <QuantityField
                          name="alternative_e_value"
                          placeholder="e.g. 35"
                          unit="gCO₂eq/MJ"
                          maxWholeDigits={MAX_E_VALUE_DIGITS}
                          maxDecimals={MAX_E_VALUE_DECIMALS}
                        />
                        {props.submitCount > 0 &&
                          props.errors?.alternative_e_value && (
                            <Label className="error d-block">
                              {props.errors.alternative_e_value}
                            </Label>
                          )}
                      </>
                    ) : (
                      <>
                        <Label className="mb-0 asteric">
                          Certified GHG intensity
                        </Label>
                        <div className="input-group mb-2">
                          <Field
                            type="text"
                            inputMode="decimal"
                            name="alternative_intensity_override"
                            className="form-control"
                            placeholder="e.g. 20"
                          />
                          <div className="input-group-text">gCO₂eq/MJ</div>
                        </div>
                        {props.submitCount > 0 &&
                          props.errors?.alternative_intensity_override && (
                            <Label className="error d-block">
                              {props.errors.alternative_intensity_override}
                            </Label>
                          )}
                      </>
                    )}
                  </Col>
                  <Col xs={12} md={6} lg={3}>
                    <Label
                      className={`mb-0${
                        alternativeFactor?.lcv ? "" : " asteric"
                      }`}
                    >
                      {usesEValue(alternativeFactor)
                        ? "LCV (RED II Annex III)"
                        : "LCV"}
                    </Label>
                    <QuantityField
                      name="alternative_lcv_override"
                      placeholder="e.g. 0.044"
                      unit="MJ/g"
                      maxWholeDigits={MAX_LCV_DIGITS}
                      maxDecimals={MAX_LCV_DECIMALS}
                    />
                    <LcvEcho value={props.values.alternative_lcv_override} />
                    {props.submitCount > 0 &&
                      props.errors?.alternative_lcv_override && (
                        <Label className="error d-block">
                          {props.errors.alternative_lcv_override}
                        </Label>
                      )}
                  </Col>
                </>
              )}
            </Row>

            {/* ------------------------------------------------ live figures */}
            <LivePreview result={preview} error={previewError} />

            <Row>
              <Col>
                <div className="form-actions mt-4">
                  <Button className="btn btn-primary" type="submit">
                    Calculate
                  </Button>
                  <Button
                    className="btn btn-primary"
                    type="reset"
                    onClick={() => {
                      props.resetForm({ values: initialValues });
                      setPreview(null);
                      setPreviewError("");
                    }}
                  >
                    Reset
                  </Button>
                </div>
              </Col>
            </Row>

            {modalState && (
              <FuelEUResultsModal
                state={modalState}
                setState={setModalState}
                id={recordId}
                result={savedResult}
                vesselName={props.values.name_of_vessel}
                year={props.values.year}
              />
            )}
          </Form>
          );
        }}
      </Formik>
    </CardBody>
  );
};

/**
 * The mix's combined well-to-wake intensity — one figure for every fuel entered.
 *
 * This replaced a per-fuel intensity column. Equation (1) produces a single
 * energy-weighted number for the mix and nothing per fuel; the column was a
 * display convenience, and it invited the reader to average the three-decimal
 * values themselves and land ~1.7e-4 gCO₂eq/MJ off the real answer with no way to
 * tell rounding from a defect.
 *
 * The warning below it is the part that must not be lost with the column. A fuel
 * with no published factors and no certified value cannot be priced, and the API
 * excludes it from both sides of the equation rather than letting it count as
 * carbon-free — so the figure shown describes a smaller mix than the ship
 * actually burnt, and saying so is the whole point.
 */
const MixIntensity = ({ result }: { result: FuelEUResult | null }) => {
  if (!result || !result.fuel_energy) return null;

  // Equal only when there is neither shore power nor an RFNBO. Showing the
  // difference is the point: this box answers "how clean is what I bunkered",
  // and the strip below answers "what does the regulation score me at".
  const diverges =
    Math.abs(result.attained_intensity - result.fuel_intensity) > 0.0005;

  return (
    <div className="fueleu-mix">
      <div className="fueleu-mix__figure">
        <span className="fueleu-mix__label">
          Well-to-wake intensity of this fuel mix
        </span>
        <span className="fueleu-mix__value">
          {formatNumber(result.fuel_intensity, 3)}
          <em>gCO₂eq/MJ</em>
        </span>
      </div>
      {result.fuel_wtt_intensity !== null &&
        result.fuel_ttw_intensity !== null && (
          <div className="fueleu-mix__split">
            well-to-tank {formatNumber(result.fuel_wtt_intensity, 3)}
            {"  +  "}
            tank-to-wake {formatNumber(result.fuel_ttw_intensity, 3)}
          </div>
        )}
      {diverges && (
        <div className="fueleu-mix__split">
          Attained intensity is{" "}
          <strong>{formatNumber(result.attained_intensity, 3)}</strong> — shore
          power and the RFNBO reward count toward the regulation&rsquo;s energy
          total but not toward the fuel&rsquo;s own intensity.
        </div>
      )}
      {result.has_unpriced_fuel && (
        <div className="fueleu-mix__warning">
          {result.unpriced_fuels === 1 ? "One fuel" : `${result.unpriced_fuels} fuels`}
          {" "}in this mix
          {result.unpriced_energy > 0
            ? ` (${formatEnergy(result.unpriced_energy)})`
            : ""}{" "}
          cannot be priced — no published emission factors, and no E value or
          certified intensity entered. Left out of this figure entirely, so it
          describes less fuel than the ship burns.
        </div>
      )}
    </div>
  );
};

/** The four figures the form recomputes as you type. */
const LivePreview = ({
  result,
  error,
}: {
  result: FuelEUResult | null;
  error: string;
}) => {
  if (error) {
    return <div className="fueleu-live fueleu-live--error">{error}</div>;
  }
  if (!result || !result.total_energy) {
    return (
      <div className="fueleu-live fueleu-live--empty">
        Add a fuel and a quantity to see the compliance balance.
      </div>
    );
  }

  const status = result.is_compliant ? "Surplus" : "Deficit";

  return (
    <div className="fueleu-live">
      <div className="fueleu-live__item">
        <span className="fueleu-live__label">Attained intensity</span>
        <span className="fueleu-live__value">
          {formatNumber(result.attained_intensity, 3)}
          <em>gCO₂eq/MJ</em>
        </span>
      </div>
      <div className="fueleu-live__item">
        <span className="fueleu-live__label">Target</span>
        <span className="fueleu-live__value">
          {formatNumber(result.target_intensity, 3)}
          <em>gCO₂eq/MJ</em>
        </span>
      </div>
      <div
        className={`fueleu-live__item fueleu-live__item--${balanceRatingClass(
          status
        )}`}
      >
        <span className="fueleu-live__label">Compliance balance</span>
        <span className="fueleu-live__value">
          {formatBalance(result.compliance_balance)}
          <em>tCO₂eq</em>
        </span>
      </div>
      <div className="fueleu-live__item">
        <span className="fueleu-live__label">Annex IV penalty</span>
        <span className="fueleu-live__value">
          {formatEuro(result.penalty_eur)}
        </span>
      </div>
    </div>
  );
};

/**
 * Keeps the live figures in step with the form.
 *
 * A component rather than an effect in the parent because it needs Formik's
 * `values` on every keystroke, and hoisting that into the parent's state would
 * re-render the entire form on each one.
 *
 * The request is debounced and the response is guarded by a sequence number: a
 * slow reply for an older set of inputs must not overwrite a fast one for the
 * current set, which is exactly what happens on a form where each keystroke
 * fires a request.
 */
const PreviewSync = ({
  values,
  onResult,
  onError,
}: {
  values: any;
  onResult: (result: FuelEUResult | null) => void;
  onError: (message: string) => void;
}) => {
  const latest = useRef(0);
  // Built from `buildPayload`, not from a hand-listed set of fields, so the
  // object watched here and the object posted are the same one by
  // construction. The hand-listed version dropped `alternative_e_value` the day
  // it was added: the field was on screen, held its value, and never reached
  // the request - no error, and the only symptom was an optimiser quietly
  // pricing the fuel off its factor row instead.
  //
  // Serialised, so the effect re-runs on a changed *value* rather than on the
  // new object identity Formik hands back for every render.
  const payload = buildPayload(values);
  // The two identity fields are the deliberate exclusions: they do not enter
  // the calculation, and leaving them in would re-fire the preview on every
  // keystroke in the vessel name.
  const { name_of_vessel, imo_number, ...calculationInputs } = payload;
  const signature = JSON.stringify(calculationInputs);

  useEffect(() => {
    const payload = JSON.parse(signature);
    const usable = (payload.fuels ?? []).length > 0;

    if (!usable) {
      onResult(null);
      onError("");
      return;
    }

    const sequence = ++latest.current;
    const timer = setTimeout(() => {
      postPreview(payload)
        .then((result) => {
          if (sequence !== latest.current) return;
          onResult(result);
          onError("");
        })
        .catch(() => {
          if (sequence !== latest.current) return;
          onError("Could not reach the calculator. Check your connection.");
        });
    }, PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return null;
};

export default FuelEUCalculator;
